import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CalendarEntry, getMyCalendar } from '../api/calendar';
import { PortalPage } from '../../../design-system/portal';
import { closingLabel } from '../vocabulary/learningVocabulary';

/**
 * S7.1 / TCH-13-FR-04 — the teacher's calendar of confirmed sessions.
 *
 * `GET /me/calendar` has existed since P2 and had no caller at all: the Wave A
 * audit found a teacher could set their availability and then had no way to see
 * what had actually been booked into it. Their only view of a session was
 * inside the enrollment it belonged to, one enrollment at a time.
 *
 * Two acceptance criteria shape what is here:
 *
 *  - **"Shows confirmed/upcoming sessions from authoritative booking state."**
 *    Nothing is derived locally. The endpoint returns Confirmed and Rescheduled
 *    bookings only, already ordered, and this screen groups them by day and
 *    renders them. A session that is still Requested is not on the calendar
 *    because it is not yet an appointment.
 *  - **"Re-entry/refresh reconstructs the same schedule."** The week being
 *    viewed lives in the URL, not in component state, so a refresh, a deep link
 *    or a back button all land on the same week rather than snapping to today.
 */

/**
 * Every date here is a calendar day in the teacher's own timezone, never a UTC
 * one. Mixing the two is how a Monday anchor becomes the previous week west of
 * Greenwich, so the helpers below build and read Dates with local fields only,
 * and step by days rather than by milliseconds so a DST boundary cannot shift a
 * week onto the wrong day.
 */
const pad = (n: number) => String(n).padStart(2, '0');

const localYmd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Local midnight of a YYYY-MM-DD, as opposed to `new Date(s)`, which reads UTC. */
const parseYmd = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Monday of the week containing `d`, as YYYY-MM-DD. */
export function weekStart(d: Date): string {
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = local.getDay(); // 0 = Sunday
  local.setDate(local.getDate() - (dow === 0 ? 6 : dow - 1));
  return localYmd(local);
}

/** The week anchor from the URL, falling back to this week when absent or unparseable. */
export function anchorFrom(param: string | null): string {
  if (param && /^\d{4}-\d{2}-\d{2}$/.test(param)) {
    const parsed = parseYmd(param);
    // `new Date(2026, 12, 45)` is not NaN — it rolls over into 2027. Round-trip
    // the parse so a shaped-but-impossible date falls back rather than silently
    // showing a week months away from the one that was asked for.
    if (!Number.isNaN(parsed.getTime()) && localYmd(parsed) === param) return weekStart(parsed);
  }
  return weekStart(new Date());
}

const shiftWeek = (anchor: string, weeks: number): string => {
  const d = parseYmd(anchor);
  d.setDate(d.getDate() + weeks * 7);
  return localYmd(d);
};

const dayLabel = (d: Date) =>
  d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });

const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

const rangeLabel = (start: string, end?: string) =>
  end ? `${timeLabel(start)} – ${timeLabel(end)}` : timeLabel(start);

const sessionTitle = (e: CalendarEntry) =>
  e.milestoneSequence === 8 ? closingLabel() : `Sesión ${e.milestoneSequence}`;

/** Entries grouped into calendar days, in order, keyed by local date. */
export function groupByDay(
  entries: CalendarEntry[],
): Array<{ date: string; items: CalendarEntry[] }> {
  // A record rather than a Map: the frontend compiles below ES2015, where
  // iterating a Map needs downlevelIteration.
  const days: Record<string, CalendarEntry[]> = {};
  for (const entry of entries) {
    const local = new Date(entry.scheduledStart);
    if (Number.isNaN(local.getTime())) continue;
    const key = localYmd(local);
    if (days[key]) days[key].push(entry);
    else days[key] = [entry];
  }
  return Object.keys(days)
    .sort()
    .map((date) => ({
      date,
      items: days[date].sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart)),
    }));
}

const card: React.CSSProperties = {
  border: '1px solid #dee2e6',
  borderRadius: 10,
  padding: 14,
  background: '#fff',
};

const navButton: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid #ced4da',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 14,
};

const TeacherCalendar: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const anchor = anchorFrom(searchParams.get('week'));

  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The bounds are instants, and the week they describe is the teacher's local
  // one: midnight Monday where they are, through midnight the following Monday.
  // Sending the anchor date with a Z would ask for a UTC week, which west of
  // Greenwich drops Sunday evening's sessions off the end.
  const from = parseYmd(anchor).toISOString();
  const to = parseYmd(shiftWeek(anchor, 1)).toISOString();

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    getMyCalendar(from, to)
      .then((res) => {
        if (!active) return;
        setEntries(res.calendar || []);
        setError(null);
      })
      .catch((e: any) => {
        if (active) setError(e?.message || 'No pudimos cargar tu calendario.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [from, to]);

  useEffect(() => load(), [load]);

  const days = useMemo(() => groupByDay(entries), [entries]);
  const goToWeek = (weeks: number) => setSearchParams({ week: shiftWeek(anchor, weeks) });
  const isThisWeek = anchor === weekStart(new Date());

  return (
    <PortalPage screenId="s_schedule">
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <h1 style={{ marginBottom: 4 }}>Mi calendario</h1>
        <p style={{ color: '#666', marginTop: 0 }}>
          Tus sesiones confirmadas. Para ofrecer o quitar horarios, edita{' '}
          <Link to="/teacher/availability">tu disponibilidad</Link>.
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            margin: '18px 0',
          }}
        >
          <button onClick={() => goToWeek(-1)} style={navButton}>
            ← Semana anterior
          </button>
          <button onClick={() => goToWeek(1)} style={navButton}>
            Semana siguiente →
          </button>
          {!isThisWeek && (
            <button
              onClick={() => setSearchParams({ week: weekStart(new Date()) })}
              style={navButton}
            >
              Esta semana
            </button>
          )}
          <span style={{ color: '#666', fontSize: 14 }}>
            Semana del {dayLabel(parseYmd(anchor))}
          </span>
        </div>

        {loading && <p>Cargando calendario…</p>}

        {!loading && error && (
          <div style={{ ...card, borderColor: '#f1aeae', background: '#fff5f5' }}>
            <p style={{ margin: 0, color: '#c92a2a' }}>{error}</p>
            <button onClick={load} style={{ ...navButton, marginTop: 10 }}>
              Reintentar
            </button>
          </div>
        )}

        {!loading && !error && days.length === 0 && (
          <div style={card}>
            <p style={{ margin: 0 }}>No tienes sesiones confirmadas esta semana.</p>
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: 14 }}>
              Las sesiones aparecen aquí cuando una familia confirma un horario. Puedes revisar las
              solicitudes pendientes desde <Link to="/teacher/dashboard">tu panel</Link>.
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          days.map((day) => (
            <section key={day.date} style={{ marginBottom: 22 }}>
              <h2 style={{ fontSize: 15, textTransform: 'capitalize', marginBottom: 10 }}>
                {dayLabel(parseYmd(day.date))}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {day.items.map((entry) => (
                  <div
                    key={entry.bookingId}
                    style={{
                      ...card,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 12,
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {`${rangeLabel(entry.scheduledStart, entry.scheduledEnd)} · ${sessionTitle(
                          entry,
                        )}`}
                      </div>
                      <div style={{ color: '#666', fontSize: 14 }}>
                        {`${entry.courseTitle || 'Curso'}${
                          entry.childFirstName ? ` · ${entry.childFirstName}` : ''
                        }`}
                      </div>
                      {entry.state === 'Rescheduled' && (
                        <div style={{ color: '#e8590c', fontSize: 13, marginTop: 4 }}>
                          Reprogramación pendiente de confirmar
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <Link
                        to={`/enrollments/${entry.enrollmentId}/sessions/${entry.milestoneSequence}`}
                      >
                        Ver sesión
                      </Link>
                      <Link to={`/enrollments/${entry.enrollmentId}`}>Ver inscripción</Link>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
      </div>
    </PortalPage>
  );
};

export default TeacherCalendar;
